import { doc, getDoc, updateDoc } from 'firebase/firestore';

/** Shown when a user has a collage but no `accuracy` on their user doc. */
export const VIBE_ACCURACY_FALLBACK_WITH_COLLAGE = 38;

/** Added to `users/{uid}.accuracy` on each fox daily vibe check (once per 3am period). */
export const DAILY_VIBE_ACCURACY_INCREMENT = 0.5;

/** Maximum stored vibe accuracy from daily fox checks. */
export const VIBE_ACCURACY_CAP = 77;

/** Random integer percent for new users (38–42 inclusive). */
export function randomInitialVibeAccuracy() {
  return 38 + Math.floor(Math.random() * 5);
}

/**
 * Resolve stored doc value to a display percent, or null if none should show.
 * Vibe accuracy is only meaningful for users with a collage, so collage-less
 * users never see a number even if their doc carries a stored `accuracy`.
 */
export function displayVibeAccuracyPercent(storedAccuracy, hasCollage) {
  if (!hasCollage) return null;
  if (typeof storedAccuracy === 'number' && Number.isFinite(storedAccuracy)) {
    return Math.round(Math.min(VIBE_ACCURACY_CAP, storedAccuracy));
  }
  return VIBE_ACCURACY_FALLBACK_WITH_COLLAGE;
}

function resolveStoredVibeAccuracyBase(storedAccuracy) {
  if (typeof storedAccuracy === 'number' && Number.isFinite(storedAccuracy)) {
    return storedAccuracy;
  }
  return VIBE_ACCURACY_FALLBACK_WITH_COLLAGE;
}

/**
 * Bump vibe accuracy after a fox daily-check response (first response per period only).
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {string} userId
 */
export async function incrementVibeAccuracyForDailyCheck(
  firestore,
  userId,
  incrementBy = DAILY_VIBE_ACCURACY_INCREMENT,
) {
  if (!userId) return;

  const userRef = doc(firestore, 'users', userId);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return;

  const base = resolveStoredVibeAccuracyBase(snap.data()?.accuracy);
  const next = Math.min(VIBE_ACCURACY_CAP, base + incrementBy);

  await updateDoc(userRef, {
    accuracy: next,
    updatedAt: new Date(),
  });
}
